import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import axiosClient from "../../api/axiosClient";
import DataTable from "../brand/DataTable";

const STATUS_LABEL = {
  eligible: "No access",
  pending: "Invite pending",
  has_access: "Has access",
  no_email: "No email",
};

export default function InviteOwnersButton({ unitId, propertyId, onInvited }) {
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [selected, setSelected] = useState(() => new Set());

  const invite = async (typedEmail) => {
    setBusy(true);
    try {
      const url = unitId
        ? `/units/${unitId}/client-invites/`
        : `/properties/${propertyId}/client-invites/`;
      const response = await axiosClient.post(url, typedEmail ? { email: typedEmail } : {});
      const count = response.data.results?.length || 0;
      const skipped = response.data.skipped?.length || 0;
      const message = `Invites queued: ${count}. Skipped: ${skipped}.`;
      if (onInvited) onInvited(message);
      else setToast(message);
      setOpen(false);
      setEmail("");
    } catch (error) {
      setToast(error.response?.data?.error || "Invite failed.");
    } finally {
      setBusy(false);
    }
  };

  const loadOwners = async (nextPage, nextSize) => {
    const response = await axiosClient.get(`/properties/${propertyId}/owner-candidates/`, {
      params: { page: nextPage + 1, pageSize: nextSize },
    });
    setRows(response.data.results || []);
    setTotal(response.data.total || 0);
  };

  const openPropertyDialog = async () => {
    setSelected(new Set());
    setPage(0);
    setOpen(true);
    try {
      await loadOwners(0, pageSize);
    } catch (error) {
      setToast(error.response?.data?.error || "Could not load owners.");
    }
  };

  const sendSelected = async () => {
    setBusy(true);
    try {
      const response = await axiosClient.post(`/properties/${propertyId}/client-invites/`, {
        emails: Array.from(selected),
      });
      const count = response.data.results?.length || 0;
      const skipped = response.data.skipped?.length || 0;
      const message = `Invites queued: ${count}. Skipped: ${skipped}.`;
      if (onInvited) onInvited(message);
      else setToast(message);
      setOpen(false);
      setSelected(new Set());
    } catch (error) {
      setToast(error.response?.data?.error || "Invite failed.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (rowEmail) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(rowEmail)) next.delete(rowEmail);
      else next.add(rowEmail);
      return next;
    });
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<PersonAddIcon />}
        onClick={() => (unitId ? setOpen(true) : openPropertyDialog())}
        disabled={busy}
      >
        + Invite owners
      </Button>
      {unitId ? (
        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>Invite this owner</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Email if the ownership record has none"
              fullWidth
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => invite(email)} disabled={busy}>+ Send invite</Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Invite owners</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Owners who do not have portal access for a unit can be invited. Pending invites are left as they are.
            </Typography>
            <DataTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Owner</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Units</TableCell>
                  <TableCell>Access</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>No current owners.</TableCell>
                  </TableRow>
                )}
                {rows.map((row) => {
                  const key = row.email || `unit-${row.units?.[0]?.id}`;
                  const canSelect = row.status === "eligible" && row.email;
                  return (
                    <TableRow key={key}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={Boolean(row.email && selected.has(row.email))}
                          disabled={!canSelect}
                          onChange={() => toggle(row.email)}
                          inputProps={{ "aria-label": `Select ${row.owner_name || row.email || "owner"}` }}
                        />
                      </TableCell>
                      <TableCell>{row.owner_name || "—"}</TableCell>
                      <TableCell>{row.email || "—"}</TableCell>
                      <TableCell>{(row.units || []).map((unit) => unit.name).join(", ")}</TableCell>
                      <TableCell>{STATUS_LABEL[row.status] || row.status}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </DataTable>
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[10]}
              onPageChange={async (_, value) => {
                setPage(value);
                await loadOwners(value, pageSize);
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={sendSelected} disabled={busy || selected.size === 0} startIcon={<PersonAddIcon />}>
              + Send invites
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Snackbar open={Boolean(toast)} message={toast} autoHideDuration={4000} onClose={() => setToast("")} />
    </>
  );
}
